import type {
  AttachmentLocale,
  LocalizationBundleIdentity,
} from '../../../../../shared/localization.ts';
import {
  LOCALE_BUNDLE_READY_METHOD,
  LOCALE_COMMITTED_METHOD,
  type LOCALE_SOURCE_FALLBACK_METHOD,
} from '../../../../../shared/ws-protocol.ts';
import type { LocalizationStore } from './localization-store.ts';
import {
  type LocalePreferenceStorage,
  loadExplicitLocalePreference,
  refreshExplicitLocalePreference,
  saveExplicitLocalePreference,
} from './explicit-locale-preference.ts';

type LocalizationNotificationMethod =
  | typeof LOCALE_COMMITTED_METHOD
  | typeof LOCALE_BUNDLE_READY_METHOD
  | typeof LOCALE_SOURCE_FALLBACK_METHOD;

interface LocalizationTransport {
  onLocalization(handler: (method: LocalizationNotificationMethod, params: unknown) => void): void;
  onSessionJoined(handler: (localization: unknown, connectionId?: string) => void): void;
  localeHint(locale: string, activeBundle?: LocalizationBundleIdentity): Promise<unknown>;
  activateLocale(identity: LocalizationBundleIdentity): Promise<{ accepted: boolean }>;
}

/** Connects one browser attachment to session locale events and its origin-local preference. */
export function wireLocalizationSession(
  store: LocalizationStore,
  transport: LocalizationTransport,
  storage?: LocalePreferenceStorage,
): () => AttachmentLocale {
  let baselineRevision: number | null = null;
  let currentConnectionId: string | undefined;
  let explicitPreference = loadExplicitLocalePreference(storage);

  transport.onLocalization((method, params) => {
    if (method === LOCALE_COMMITTED_METHOD) {
      if (store.receiveCommitted(params)) {
        const committedPreference = store.persistableExplicitLocale();
        const revision = store.desiredLocale?.revision ?? null;
        if (
          committedPreference &&
          baselineRevision !== null &&
          revision !== null &&
          revision > baselineRevision
        ) {
          explicitPreference = committedPreference;
          saveExplicitLocalePreference(committedPreference, storage);
        }
        baselineRevision = revision;
      }
      return;
    }
    if (method === LOCALE_BUNDLE_READY_METHOD) {
      store.receiveBundleReady(params);
      return;
    }
    store.receiveSourceFallback(params);
  });

  transport.onSessionJoined((localization, connectionId) => {
    if (connectionId && currentConnectionId === connectionId) {
      return;
    }
    currentConnectionId = connectionId;
    store.beginSession();
    store.receiveCommitted(localization);
    baselineRevision = store.desiredLocale?.revision ?? null;
  });

  store.setActivationSender((identity) => transport.activateLocale(identity));
  store.setResolutionRequester((locale, activeBundle) =>
    transport.localeHint(locale, activeBundle),
  );

  return () => {
    explicitPreference = refreshExplicitLocalePreference(explicitPreference, storage);
    return {
      locale: explicitPreference ?? store.navigatorLocale(),
      source: explicitPreference ? 'explicit' : 'navigator',
    };
  };
}
